import {
  Box, Button, Card, Flex, Grid, Icon, Text, VStack, HStack,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Switch, FormControl, FormLabel,
  useDisclosure,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useToast } from '@chakra-ui/react'
import ModalInput from '../forms/modalForms/modalInput'
import ModalTextarea from '../forms/modalForms/modalTextarea'
import { PlusIcon, Edit05Icon, Trash04Icon } from '../../assets/icons'
import { getAdminPlans, createAdminPlan, updateAdminPlan, deleteAdminPlan } from '../../api/billing'
import { PlanInfoAdmin } from '../../types/billingTypes'
import handleToast from '../HandleToast'
import { text as textStyles } from '../../themes/styles'

const emptyPlan: Partial<PlanInfoAdmin> = {
  key: '',
  name_en: '',
  name_nl: '',
  description_en: '',
  description_nl: '',
  price_per_seat: '',
  stripe_price_id: '',
  features: [],
  is_active: true,
  sort_order: 0,
}

const AVAILABLE_FEATURES = [
  'workforce', 'classifications', 'explore', 'ai_assistant',
  'scheduling', 'faq', 'analytics', 'recruitment', 'evaluations',
  'time_tracking', 'documents_signing', 'outflow', 'assets', 'payroll',
]

const PlansManagement: React.FC = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [editPlan, setEditPlan] = useState<Partial<PlanInfoAdmin>>(emptyPlan)
  const [isNew, setIsNew] = useState(true)

  const { data: plans, isLoading } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: getAdminPlans,
  })

  const saveMutation = useMutation({
    mutationFn: (data: Partial<PlanInfoAdmin>) =>
      isNew ? createAdminPlan(data) : updateAdminPlan(data.id!, data),
    onSuccess: () => {
      handleToast(toast, true, t('common.states.success'), isNew ? t('settings.plans.created', 'Plan created') : t('settings.plans.updated', 'Plan updated'))
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
      queryClient.invalidateQueries({ queryKey: ['billing-plans'] })
      onClose()
    },
    onError: (error: any) => handleToast(toast, false, t('common.states.error'), error.message),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteAdminPlan,
    onSuccess: () => {
      handleToast(toast, true, t('common.states.success'), t('settings.plans.deleted', 'Plan deleted'))
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] })
      queryClient.invalidateQueries({ queryKey: ['billing-plans'] })
    },
    onError: (error: any) => handleToast(toast, false, t('common.states.error'), error.message),
  })

  const openNew = () => {
    setEditPlan({ ...emptyPlan })
    setIsNew(true)
    onOpen()
  }

  const openEdit = (plan: PlanInfoAdmin) => {
    setEditPlan({ ...plan })
    setIsNew(false)
    onOpen()
  }

  const features = Array.isArray(editPlan.features) ? editPlan.features : []

  const toggleFeature = (feature: string) => {
    setEditPlan((prev) => {
      const current = Array.isArray(prev.features) ? prev.features : []
      return {
        ...prev,
        features: current.includes(feature)
          ? current.filter((f: string) => f !== feature)
          : [...current, feature],
      }
    })
  }

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditPlan((prev) => ({ ...prev, [field]: e.target.value }))
  }

  return (
    <Card width="100%" p="0">
      <Flex justifyContent="space-between" alignItems="center" height="64px" px="20px">
        <Text {...textStyles.sectionTitle}>
          {t('settings.plans.title', 'Subscription Plans')}
        </Text>
        <Button
          variant="mono"
          size="sm"
          h="32px"
          px="14px"
          onClick={openNew}
        >
          <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="white" mr={1}>
            <PlusIcon />
          </Icon>
          {t('settings.plans.addPlan', 'Add Plan')}
        </Button>
      </Flex>

      <VStack align="stretch" spacing={0} px="20px" pb="20px">
        {isLoading && <Text {...textStyles.small} color="solid.400" p={4}>{t('common.states.loading', 'Loading...')}</Text>}
        {plans?.map((plan) => (
          <Flex
            key={plan.id}
            justify="space-between"
            align="center"
            py="12px"
            px="16px"
            borderBottom="1px solid"
            borderColor="solid.25"
            _last={{ borderBottom: 'none' }}
          >
            <Box flex={1}>
              <HStack spacing={3} mb={1}>
                <Text {...textStyles.heading}>{plan.name_en}</Text>
                <Box
                  px="8px" py="2px" borderRadius="full"
                  bg={plan.is_active ? 'success.0' : 'solid.25'}
                  color={plan.is_active ? 'success.100' : 'solid.400'}
                  border="1px solid"
                  borderColor={plan.is_active ? 'success.50' : 'solid.100'}
                >
                  <Text {...textStyles.tiny} color="inherit">
                    {plan.is_active ? t('common.states.active', 'Active') : t('common.states.inactive', 'Inactive')}
                  </Text>
                </Box>
              </HStack>
              <HStack spacing={4}>
                <Text {...textStyles.tiny} color="solid.400">
                  €{plan.price_per_seat}/{t('settings.plans.seat', 'seat')} · {(Array.isArray(plan.features) ? plan.features : []).length} {t('settings.plans.features', 'features')} · {t('settings.plans.sortOrder', 'Order')}: {plan.sort_order}
                </Text>
                {plan.stripe_price_id ? (
                  <Text {...textStyles.tiny} color="primary.100">{plan.stripe_price_id}</Text>
                ) : (
                  <Text {...textStyles.tiny} color="error.100">{t('settings.plans.noStripePrice', 'No Stripe price')}</Text>
                )}
              </HStack>
            </Box>
            <HStack spacing={2}>
              <Button variant="ghost" size="xs" onClick={() => openEdit(plan)}>
                <Icon w="14px" h="14px" viewBox="0 0 24 24" fill="none" color="solid.500">
                  <Edit05Icon />
                </Icon>
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  if (window.confirm(t('settings.plans.confirmDelete', 'Delete this plan?'))) {
                    deleteMutation.mutate(plan.id)
                  }
                }}
              >
                <Icon w="14px" h="14px" viewBox="0 0 24 24" fill="none" color="error.100">
                  <Trash04Icon />
                </Icon>
              </Button>
            </HStack>
          </Flex>
        ))}
        {!isLoading && (!plans || plans.length === 0) && (
          <Text {...textStyles.small} color="solid.400" p={4} textAlign="center">
            {t('settings.plans.noPlans', 'No plans configured yet.')}
          </Text>
        )}
      </VStack>

        <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
          <ModalOverlay />
          <ModalContent borderRadius="12px">
          <ModalHeader>
            <Text {...textStyles.sectionTitle}>
              {isNew ? t('settings.plans.addPlan', 'Add Plan') : t('settings.plans.editPlan', 'Edit Plan')}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="16px">
            <VStack spacing={4} align="stretch">
              <Grid templateColumns="1fr 1fr" gap={3}>
                <ModalInput
                  label={t('settings.plans.key', 'Key')}
                  name="key"
                  value={editPlan.key || ''}
                  onChange={handleChange('key')}
                  isDisabled={!isNew}
                  placeholder={t('settings.plans.keyPlaceholder', 'e.g. basic')}
                  required
                />
                <ModalInput
                  label={t('settings.plans.pricePerSeat', 'Price per seat (€)')}
                  name="price_per_seat"
                  type="number"
                  value={String(editPlan.price_per_seat || '')}
                  onChange={handleChange('price_per_seat')}
                  required
                />
              </Grid>
              <Grid templateColumns="1fr 1fr" gap={3}>
                <ModalInput
                  label={t('settings.plans.nameEn', 'Name (EN)')}
                  name="name_en"
                  value={editPlan.name_en || ''}
                  onChange={handleChange('name_en')}
                  required
                />
                <ModalInput
                  label={t('settings.plans.nameNl', 'Name (NL)')}
                  name="name_nl"
                  value={editPlan.name_nl || ''}
                  onChange={handleChange('name_nl')}
                />
              </Grid>
              <Grid templateColumns="1fr 1fr" gap={3}>
                <ModalTextarea
                  label={t('settings.plans.descEn', 'Description (EN)')}
                  name="description_en"
                  value={editPlan.description_en || ''}
                  onChange={handleChange('description_en') as any}
                  rows={2}
                />
                <ModalTextarea
                  label={t('settings.plans.descNl', 'Description (NL)')}
                  name="description_nl"
                  value={editPlan.description_nl || ''}
                  onChange={handleChange('description_nl') as any}
                  rows={2}
                />
              </Grid>
              <Grid templateColumns="1fr 1fr" gap={3}>
                <ModalInput
                  label={t('settings.plans.stripePriceId', 'Stripe Price ID')}
                  name="stripe_price_id"
                  value={editPlan.stripe_price_id || ''}
                  onChange={handleChange('stripe_price_id')}
                  placeholder={t('settings.plans.stripePriceIdPlaceholder', 'price_...')}
                />
                <ModalInput
                  label={t('settings.plans.sortOrder', 'Sort Order')}
                  name="sort_order"
                  type="number"
                  value={String(editPlan.sort_order ?? 0)}
                  onChange={(e) => setEditPlan((p) => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
                />
              </Grid>
              <FormControl display="flex" alignItems="center">
                <FormLabel {...textStyles.label} mb={0}>
                  {t('common.states.active', 'Active')}
                </FormLabel>
                <Switch
                  isChecked={editPlan.is_active ?? true}
                  onChange={(e) => setEditPlan((p) => ({ ...p, is_active: e.target.checked }))}
                  colorScheme="purple"
                />
              </FormControl>
              <Box>
                <Text {...textStyles.label} mb={2}>
                  {t('settings.plans.features', 'Features')}
                </Text>
                <Grid templateColumns="1fr 1fr" gap={2}>
                  {AVAILABLE_FEATURES.map((feature) => {
                    const isSelected = features.includes(feature)
                    return (
                      <Flex
                        key={feature}
                        align="center"
                        gap={2}
                        px={3}
                        py={2}
                        borderRadius="6px"
                        border="1px solid"
                        borderColor={isSelected ? 'primary.100' : 'solid.50'}
                        bg={isSelected ? 'primary.0' : 'white'}
                        cursor="pointer"
                        onClick={() => toggleFeature(feature)}
                      >
                        <Text {...textStyles.tiny} color={isSelected ? 'primary.100' : 'solid.500'}>
                          {t(`settings.billing.features.${feature}`, feature)}
                        </Text>
                      </Flex>
                    )
                  })}
                </Grid>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter gap="8px">
            <Button variant="ghost" size="sm" onClick={onClose}>
              {t('common.actions.cancel')}
            </Button>
            <Button
              variant="mono"
              size="sm"
              onClick={() => saveMutation.mutate(editPlan)}
              isLoading={saveMutation.isPending}
              isDisabled={!editPlan.key || !editPlan.name_en || !editPlan.price_per_seat}
            >
              {t('common.actions.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  )
}

export default PlansManagement
